import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { apiFetch } from '../utils/api';
import { Button } from './ui/button';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { AnalyzeButton } from './AnalyzeButton';
import { AddItemButton } from './AddItemButton';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { PanelPageHeader } from './PanelPageHeader';
import { PanelHeaderActions } from './PanelHeaderActions';
import { ViewSwitcher, useViewModeSearchParam } from './ViewSwitcher';
import { ChaptersOutline } from './ChaptersOutline';
import { ChaptersEmptyState } from './ChaptersEmptyState';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import { DocumentChapters, type EditableChapterAnalysisField } from './DocumentChapters';
import { QuestionsTable } from './QuestionsTable';
import { RelatedEntityLink } from './RelatedEntityLink';
import { ConfirmDialog } from './ConfirmDialog';
import { EditDialogFooter } from './EditDialogFooter';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { SceneFormDialog } from './SceneFormDialog';
import type { Scene } from './Scenes';
import { removeMentionFromHtml, type RelatedEntity } from '../utils/entity-utils';
import { getRelatedEntitiesFor } from '../utils/related-entities';
import { buildSceneOrderFromChapterLists, isUnlinkedScenesContainer } from '../utils/chapter-scene-order';
import {
  createSceneLink,
  getLinkableScenes,
  getUnlinkedChapterScenes,
  isSceneLinkedToContext,
  removeSceneLinks,
  removeSceneOutboundLink,
  sceneDocumentIdOf,
  shouldSuggestMoveSceneAfterChapterLink,
  type SceneLinkContext,
} from '../utils/scene-links';
import { buildAvailableScenesTree } from '../utils/available-scenes-tree';
import type { ChapterSceneDragEndResult } from './ChaptersListView';
import { useToast } from '../contexts/ToastContext';
import { notifyOnboardingUpdate } from '../onboarding/notify-onboarding-update';
import { buildDocumentSectionPath } from '../utils/document-section-navigation';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from './ui/dialog';
import { Tabs } from './ui/tabs';
import { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { useSortableSensors } from '../hooks/use-sortable-sensors';
import { SortableDragHandle } from './SortableDragHandle';
import { CSS } from '@dnd-kit/utilities';
import { Pencil } from 'lucide-react';

interface ChapterAnalysisData {
  summary: string;
  mood: string;
  tensionLevel: number;
  actionLevel: number;
  plotQuestionsOpened: string[];
  plotQuestionsClosed: string[];
  manualChapterMatch?: {
    matchedManualChapterId: string | null;
    matchedManualChapterOrder: number | null;
    contentMatchScore: number;
    contentMatchExplanation: string;
    wordCountMatch: {
      actual: number;
      planned: number | null;
      difference: number | null;
      matchScore: number;
    };
    orderMatch: {
      actualPosition: number | null;
      plannedPosition: number | null;
      difference: number | null;
      matchScore: number;
    };
  };
}

export interface Chapter {
  id?: string;
  title: string;
  wordCount: number;
  readingTime: number;
  estimatedPages: number;
  analysis: ChapterAnalysisData | null;
  number?: number;
  order?: number;
  wordsGoal?: number;
  summary?: string;
  isManual?: boolean;
}

interface ChaptersByLevel {
  [level: number]: Chapter[];
}

export type ChaptersPanelMode = 'outline' | 'analysis';

interface ChaptersProps {
  documentId?: string;
  projectId?: string;
  isSidebar?: boolean;
  onClose?: () => void;
  initialChapterId?: string;
  mode?: ChaptersPanelMode;
  onScrollToSection?: (title: string, level: number) => void;
}

interface SortableChapterCardProps {
  chapter: Chapter;
  index: number;
  onEdit: (chapter: Chapter) => void;
  onDelete: (chapter: Chapter) => void;
  deletingChapterId: string | null;
  editingChapterId: string | null;
  token: string | null;
  masterKey: string | null;
  documentId: string | undefined;
  t: (key: string) => string;
}

export const SortableChapterCard: React.FC<SortableChapterCardProps> = ({
  chapter,
  index,
  onEdit,
  onDelete,
  deletingChapterId,
  editingChapterId,
  token,
  masterKey,
  documentId,
  t,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id || `chapter-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: isDragging ? 'none' : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      data-chapter-id={chapter.id || undefined}
      style={style}
      className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onEdit(chapter)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <SortableDragHandle attributes={attributes} listeners={listeners}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <circle cx="7" cy="5" r="1.5" />
              <circle cx="13" cy="5" r="1.5" />
              <circle cx="7" cy="10" r="1.5" />
              <circle cx="13" cy="10" r="1.5" />
              <circle cx="7" cy="15" r="1.5" />
              <circle cx="13" cy="15" r="1.5" />
            </svg>
          </SortableDragHandle>
          <span className="text-sm font-medium text-muted-foreground">
            {chapter.order || '-'}
          </span>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <RotatingDeleteButton
            onClick={() => onDelete(chapter)}
            tooltipText={t('delete_chapter')}
            ariaLabel={t('delete_chapter')}
            disabled={deletingChapterId === chapter.id}
            className="h-8 w-8 p-0"
          />
        </div>
      </div>
      <h3 className="text-lg font-semibold mb-2 text-card-foreground">{chapter.title}</h3>
      {chapter.summary && (
        <div
          className={`text-sm text-muted-foreground mb-3 line-clamp-3 ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
          dangerouslySetInnerHTML={{ __html: chapter.summary }}
        />
      )}
      {chapter.wordsGoal && (
        <div className="text-xs text-muted-foreground">
          {t('words_goal')}: {chapter.wordsGoal.toLocaleString()}
        </div>
      )}
    </div>
  );
};

interface SortableChapterRowProps {
  chapter: Chapter;
  index: number;
  isLast: boolean;
  onEdit: (chapter: Chapter) => void;
  onDelete: (chapter: Chapter) => void;
  deletingChapterId: string | null;
  editingChapterId: string | null;
  t: (key: string) => string;
}

export const SortableChapterRow: React.FC<SortableChapterRowProps> = ({
  chapter,
  index,
  isLast,
  onEdit,
  onDelete,
  deletingChapterId,
  editingChapterId,
  t,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id || `chapter-${index}` });

  return (
    <tr
      ref={setNodeRef}
      data-chapter-id={chapter.id || undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        backgroundColor: isDragging ? 'var(--muted)' : undefined,
        touchAction: isDragging ? 'none' : undefined,
      }}
      className="hover:bg-muted transition-colors"
    >
      <td
        style={{
          padding: '1rem',
          fontSize: '0.875rem',
          color: 'var(--foreground)',
          fontWeight: '500',
        }}
      >
        <div className="flex items-center gap-2">
          <SortableDragHandle
            attributes={attributes}
            listeners={listeners}
            ariaLabel={t('chapter_order')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <circle cx="7" cy="5" r="1.5" />
              <circle cx="13" cy="5" r="1.5" />
              <circle cx="7" cy="10" r="1.5" />
              <circle cx="13" cy="10" r="1.5" />
              <circle cx="7" cy="15" r="1.5" />
              <circle cx="13" cy="15" r="1.5" />
            </svg>
          </SortableDragHandle>
          <span>{chapter.order || '-'}</span>
        </div>
      </td>
      <td
        style={{
          padding: '1rem',
          fontSize: '0.875rem',
          color: 'var(--foreground)',
          fontWeight: '500',
        }}
      >
        {chapter.title}
      </td>
      <td
        style={{
          padding: '1rem',
          fontSize: '0.875rem',
          color: '#6b7280',
        }}
      >
        {chapter.wordsGoal ? chapter.wordsGoal.toLocaleString() : '-'}
      </td>
      <td
        style={{
          padding: '1rem',
          fontSize: '0.875rem',
          color: 'var(--muted-foreground)',
          maxWidth: '500px',
        }}
      >
        {chapter.summary ? (
          <div
            className={`line-clamp-2 ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
            style={{
              overflow: 'hidden',
              fontSize: '0.875rem',
              color: 'var(--muted-foreground)',
            }}
            dangerouslySetInnerHTML={{ __html: chapter.summary }}
          />
        ) : (
          '-'
        )}
      </td>
      <td
        style={{
          padding: '1rem',
          textAlign: 'center',
          fontSize: '0.875rem',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(chapter)}
            disabled={editingChapterId === chapter.id}
            className="text-[var(--scribane-black)] hover:text-[var(--scribane-black)] hover:bg-gray-100"
          >
            <Pencil size={16} />
          </Button>
          <RotatingDeleteButton
            onClick={() => onDelete(chapter)}
            tooltipText={t('delete_chapter')}
            ariaLabel={t('delete_chapter')}
            disabled={deletingChapterId === chapter.id}
            className="h-8 w-8 p-0"
          />
        </div>
      </td>
    </tr>
  );
};

export const Chapters: React.FC<ChaptersProps> = ({ documentId: propDocumentId, projectId: propProjectId, isSidebar = false, onClose, initialChapterId, mode = 'outline', onScrollToSection: onScrollToSectionProp }) => {
  const params = useParams<{ projectId?: string; documentId: string }>();
  const routeDocumentId = params.documentId;
  const routeProjectId = params.projectId;
  const documentId = propDocumentId || routeDocumentId;
  const projectId = propProjectId || routeProjectId;
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useLocale();
  const [masterKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('masterKey');
  });
  const [shareToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shareToken');
  });
  const [chaptersByLevel, setChaptersByLevel] = useState<ChaptersByLevel>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzingChapters, setAnalyzingChapters] = useState<Set<string>>(new Set());
  const [expandedAnalyses, setExpandedAnalyses] = useState<Set<string>>(new Set());
  const [analyzingDocument, setAnalyzingDocument] = useState(false);
  const [addingToOutlineChapters, setAddingToOutlineChapters] = useState<Set<string>>(new Set());
  const [documentAnalysis, setDocumentAnalysis] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChapterOrder, setNewChapterOrder] = useState<string>('');
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterWordsGoal, setNewChapterWordsGoal] = useState<string>('');
  const [newChapterSummary, setNewChapterSummary] = useState('');
  const [addingChapter, setAddingChapter] = useState(false);
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [deletingChapterId, setDeletingChapterId] = useState<string | null>(null);
  const [deleteConfirmChapter, setDeleteConfirmChapter] = useState<Chapter | null>(null);
  const [pendingScrollChapterId, setPendingScrollChapterId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useViewModeSearchParam({
    allowedModes: ['list', 'grid', 'table', 'timeline'] as const,
    defaultMode: 'list',
  });
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [documentScenes, setDocumentScenes] = useState<Scene[]>([]);
  const [projectSceneCatalog, setProjectSceneCatalog] = useState<Scene[]>([]);
  const [projectChapterCatalog, setProjectChapterCatalog] = useState<
    Array<{
      id: string;
      title: string;
      order: number;
      documentId: string;
      documentTitle: string;
    }>
  >([]);
  const [showSceneModal, setShowSceneModal] = useState(false);
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [editingSceneDocumentId, setEditingSceneDocumentId] = useState<string | null>(null);
  const [sceneLinkContext, setSceneLinkContext] = useState<SceneLinkContext | null>(null);
  const [newSceneTitle, setNewSceneTitle] = useState('');
  const [newSceneDescription, setNewSceneDescription] = useState('');
  const [savingScene, setSavingScene] = useState(false);
  const [unlinkingScene, setUnlinkingScene] = useState(false);
  const [deletingSceneId, setDeletingSceneId] = useState<string | null>(null);
  const [deleteConfirmScene, setDeleteConfirmScene] = useState<Scene | null>(null);
  const [moveScenePrompt, setMoveScenePrompt] = useState<{
    sceneId: string;
    sceneTitle: string;
    fromDocumentId: string;
    fromDocumentTitle: string;
    toDocumentId: string;
    toDocumentTitle: string;
  } | null>(null);
  const [movingScene, setMovingScene] = useState(false);
  const [chapterTimelineGroupBy, setChapterTimelineGroupBy] = useState<'none' | 'character' | 'storyline' | 'scene'>('none');
  const [chapterTimelineGroupByCharacters, setChapterTimelineGroupByCharacters] = useState<Set<string>>(new Set());
  const [chapterTimelineGroupByStorylines, setChapterTimelineGroupByStorylines] = useState<Set<string>>(new Set());
  const [chapterTimelineGroupByScenes, setChapterTimelineGroupByScenes] = useState<Set<string>>(new Set());
  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    sourceDocumentId: string | null;
    targetType: string;
    targetId: string;
    targetDocumentId: string | null;
  }>>([]);
  const { showErrorToast } = useToast();

  const closeSceneModal = useCallback((): void => {
    setShowSceneModal(false);
    setEditingSceneId(null);
    setEditingSceneDocumentId(null);
    setSceneLinkContext(null);
    setNewSceneTitle('');
    setNewSceneDescription('');
  }, []);

  const openAddSceneModal = useCallback((chapter: Chapter): void => {
    if (!chapter.id) return;
    setEditingSceneId(null);
    setEditingSceneDocumentId(null);
    setSceneLinkContext({ type: 'chapter', id: chapter.id });
    setNewSceneTitle('');
    setNewSceneDescription('');
    setShowSceneModal(true);
  }, []);

  const openEditSceneModal = useCallback((scene: Scene, chapter?: Chapter): void => {
    const sceneDocumentId = scene.documentId ?? documentId ?? null;
    setEditingSceneId(scene.id);
    setEditingSceneDocumentId(sceneDocumentId);
    setSceneLinkContext(chapter?.id ? { type: 'chapter', id: chapter.id } : null);
    setNewSceneTitle(scene.title);
    setNewSceneDescription(scene.description ?? '');
    setShowSceneModal(true);

    if (!sceneDocumentId || scene.description != null || (!token && !masterKey)) {
      return;
    }
    void (async () => {
      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${sceneDocumentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${sceneDocumentId}/scenes`;
        const response = await fetch(url, {
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!response.ok) return;
        const data = await response.json();
        const scenes = Array.isArray(data.scenes) ? (data.scenes as Scene[]) : [];
        const fullScene = scenes.find((entry) => entry.id === scene.id);
        if (!fullScene) return;
        setNewSceneTitle(fullScene.title);
        setNewSceneDescription(fullScene.description ?? '');
      } catch {
        // Keep the catalog title when the foreign document scenes cannot be loaded.
      }
    })();
  }, [documentId, masterKey, token]);

  const handleSelectExistingScene = useCallback((scene: Scene): void => {
    setEditingSceneId(scene.id);
    setEditingSceneDocumentId(scene.documentId ?? documentId ?? null);
    setNewSceneTitle(scene.title);
    setNewSceneDescription(scene.description ?? '');
  }, [documentId]);

  const fetchMentionableEntities = useCallback(async (): Promise<void> => {
    if (!projectId || (!token && !masterKey)) return;
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        setProjectSceneCatalog(
          Array.isArray(data.scenes)
            ? data.scenes.map((scene: {
                id: string;
                title: string;
                documentId: string;
                documentTitle: string;
                order: number;
              }) => ({
                id: scene.id,
                title: scene.title,
                order: scene.order,
                documentId: scene.documentId,
                documentTitle: scene.documentTitle,
              }))
            : [],
        );
        setProjectChapterCatalog(
          Array.isArray(data.chapters)
            ? data.chapters.map((chapter: {
                id: string;
                title: string;
                documentId: string;
                documentTitle: string;
                order: number;
              }) => ({
                id: chapter.id,
                title: chapter.title,
                order: chapter.order,
                documentId: chapter.documentId,
                documentTitle: chapter.documentTitle,
              }))
            : [],
        );
      } else {
        setMentionableEntities([]);
        setProjectSceneCatalog([]);
        setProjectChapterCatalog([]);
      }
    } catch {
      setMentionableEntities([]);
      setProjectSceneCatalog([]);
      setProjectChapterCatalog([]);
    }
  }, [projectId, token, masterKey]);

  useEffect(() => {
    void fetchMentionableEntities();
  }, [fetchMentionableEntities]);

  const getDocumentTitle = useCallback(
    (targetDocumentId: string): string => {
      const fromScene = projectSceneCatalog.find((scene) => scene.documentId === targetDocumentId)?.documentTitle;
      if (fromScene) return fromScene;
      const fromChapter = projectChapterCatalog.find(
        (chapter) => chapter.documentId === targetDocumentId,
      )?.documentTitle;
      if (fromChapter) return fromChapter;
      const fromMention = mentionableEntities.find(
        (entity) => entity.type === 'document' && entity.id === targetDocumentId,
      )?.label;
      return fromMention?.trim() || targetDocumentId;
    },
    [projectSceneCatalog, projectChapterCatalog, mentionableEntities],
  );

  const maybePromptMoveScene = useCallback(
    (
      scene: Pick<Scene, 'id' | 'title' | 'documentTitle'>,
      sceneDocumentId: string,
      chapterDocumentId: string,
      refsBefore: typeof entityRefs,
      refsAfter: typeof entityRefs,
    ): void => {
      if (
        !shouldSuggestMoveSceneAfterChapterLink({
          refsBefore,
          refsAfter,
          sceneId: scene.id,
          sceneDocumentId,
          chapterDocumentId,
        })
      ) {
        return;
      }
      setMoveScenePrompt({
        sceneId: scene.id,
        sceneTitle: scene.title,
        fromDocumentId: sceneDocumentId,
        fromDocumentTitle: scene.documentTitle?.trim() || getDocumentTitle(sceneDocumentId),
        toDocumentId: chapterDocumentId,
        toDocumentTitle: getDocumentTitle(chapterDocumentId),
      });
    },
    [getDocumentTitle],
  );
  const fetchEntityRefs = useCallback(async (): Promise<void> => {
    if (!projectId || (!token && !masterKey)) return;
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        setEntityRefs(data.references ?? []);
      } else {
        setEntityRefs([]);
      }
    } catch {
      setEntityRefs([]);
    }
  }, [projectId, token, masterKey]);

  useEffect(() => {
    fetchEntityRefs();
  }, [fetchEntityRefs]);

  const fetchDocumentScenes = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !documentId) return;
    try {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/scenes`;
      const response = await fetch(url, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch scenes');
      const data = await response.json();
      setDocumentScenes(Array.isArray(data.scenes) ? data.scenes : []);
    } catch (err) {
      console.error('Error fetching scenes for chapters list:', err);
      setDocumentScenes([]);
    }
  }, [token, documentId, masterKey]);

  useEffect(() => {
    fetchDocumentScenes();
  }, [fetchDocumentScenes]);

  const handleSaveScene = useCallback(async (): Promise<void> => {
    if (!newSceneTitle.trim() || savingScene || !documentId) return;
    const isEditing = editingSceneId !== null;
    const sceneDocumentId = editingSceneDocumentId ?? documentId;
    const scenesForDocument =
      sceneDocumentId === documentId
        ? documentScenes
        : projectSceneCatalog.filter((scene) => scene.documentId === sceneDocumentId);
    const order = isEditing
      ? scenesForDocument.find((s) => s.id === editingSceneId)?.order ?? 1
      : Math.max(0, ...documentScenes.map((s) => s.order ?? 0)) + 1;
    try {
      setSavingScene(true);
      const url = isEditing
        ? (masterKey
            ? `${API_BASE}/api/documents/${sceneDocumentId}/scenes/${editingSceneId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${sceneDocumentId}/scenes/${editingSceneId}`)
        : (masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes`);
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          order,
          title: newSceneTitle.trim(),
          description: (newSceneDescription || '').trim() || undefined,
        }),
      });
      if (!response.ok) throw new Error(isEditing ? 'Failed to update scene' : 'Failed to create scene');

      const data: { scene?: Scene } = await response.json().catch(() => ({}));
      const sceneId = isEditing ? editingSceneId : data.scene?.id;
      const linkedSceneDocumentId = isEditing ? sceneDocumentId : documentId;
      if (
        sceneId &&
        sceneLinkContext &&
        projectId &&
        token &&
        documentId &&
        !isSceneLinkedToContext(
          entityRefs,
          sceneId,
          linkedSceneDocumentId,
          sceneLinkContext,
          documentId,
        )
      ) {
        const linked = await createSceneLink({
          projectId,
          token,
          sceneId,
          documentId,
          sceneDocumentId: linkedSceneDocumentId,
          context: sceneLinkContext,
        });
        if (!linked) {
          showErrorToast(t('failed_to_link_scene'));
        } else if (sceneLinkContext.type === 'chapter') {
          const nextRefs = [
            ...entityRefs,
            {
              sourceType: 'scene',
              sourceId: sceneId,
              sourceDocumentId: linkedSceneDocumentId,
              targetType: 'manual_chapter',
              targetId: sceneLinkContext.id,
              targetDocumentId: documentId,
            },
          ];
          maybePromptMoveScene(
            {
              id: sceneId,
              title: newSceneTitle.trim(),
              documentTitle:
                projectSceneCatalog.find(
                  (scene) => scene.id === sceneId && scene.documentId === linkedSceneDocumentId,
                )?.documentTitle,
            },
            linkedSceneDocumentId,
            documentId,
            entityRefs,
            nextRefs,
          );
        }
      }

      await fetchDocumentScenes();
      await fetchEntityRefs();
      closeSceneModal();
    } catch (err) {
      console.error(isEditing ? 'Error updating scene:' : 'Error creating scene:', err);
      showErrorToast(isEditing ? t('failed_to_update_scene') : t('failed_to_create_scene'));
    } finally {
      setSavingScene(false);
    }
  }, [
    newSceneTitle,
    newSceneDescription,
    savingScene,
    documentId,
    editingSceneId,
    editingSceneDocumentId,
    documentScenes,
    projectSceneCatalog,
    masterKey,
    token,
    sceneLinkContext,
    entityRefs,
    projectId,
    fetchDocumentScenes,
    fetchEntityRefs,
    closeSceneModal,
    maybePromptMoveScene,
    showErrorToast,
    t,
  ]);

  const handleDeleteScene = useCallback(
    async (scene: Scene): Promise<void> => {
      if (!scene.id || deletingSceneId === scene.id) return;
      const sceneDocumentId = scene.documentId ?? editingSceneDocumentId ?? documentId;
      if (!sceneDocumentId) return;
      try {
        setDeletingSceneId(scene.id);
        const url = masterKey
          ? `${API_BASE}/api/documents/${sceneDocumentId}/scenes/${scene.id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${sceneDocumentId}/scenes/${scene.id}`;
        const response = await fetch(url, {
          method: 'DELETE',
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!response.ok) throw new Error('Failed to delete scene');
        if (editingSceneId === scene.id) {
          closeSceneModal();
        }
        await fetchDocumentScenes();
        await fetchEntityRefs();
      } catch (err) {
        console.error('Error deleting scene:', err);
        showErrorToast(t('failed_to_delete_scene'));
      } finally {
        setDeletingSceneId(null);
      }
    },
    [
      closeSceneModal,
      deletingSceneId,
      documentId,
      editingSceneDocumentId,
      editingSceneId,
      fetchDocumentScenes,
      fetchEntityRefs,
      masterKey,
      showErrorToast,
      t,
      token,
    ],
  );

  const handleConfirmMoveScene = useCallback(async (): Promise<void> => {
    const prompt = moveScenePrompt;
    if (!prompt || !token || movingScene) return;
    try {
      setMovingScene(true);
      setMoveScenePrompt(null);
      const response = await fetch(
        `${API_BASE}/api/documents/${prompt.fromDocumentId}/scenes/${prompt.sceneId}/move`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ toDocumentId: prompt.toDocumentId }),
        },
      );
      if (!response.ok) {
        throw new Error('Failed to move scene');
      }
      await fetchDocumentScenes();
      await fetchEntityRefs();
      await fetchMentionableEntities();
    } catch (err) {
      console.error('Error moving scene:', err);
      showErrorToast(t('failed_to_move_scene'));
    } finally {
      setMovingScene(false);
    }
  }, [
    moveScenePrompt,
    token,
    movingScene,
    fetchDocumentScenes,
    fetchEntityRefs,
    fetchMentionableEntities,
    showErrorToast,
    t,
  ]);

  const handleUnlinkScene = useCallback(async (): Promise<void> => {
    if (!editingSceneId || !sceneLinkContext || !documentId || !projectId || !token || unlinkingScene) {
      return;
    }
    const sceneDocumentId = editingSceneDocumentId ?? documentId;
    try {
      setUnlinkingScene(true);
      const unlinked = await removeSceneLinks({
        projectId,
        token,
        sceneId: editingSceneId,
        documentId,
        sceneDocumentId,
        context: sceneLinkContext,
      });
      if (!unlinked) {
        showErrorToast(t('failed_to_unlink_scene'));
        return;
      }
      closeSceneModal();
      await fetchDocumentScenes();
      await fetchEntityRefs();
    } catch (err) {
      console.error('Error unlinking scene:', err);
      showErrorToast(t('failed_to_unlink_scene'));
    } finally {
      setUnlinkingScene(false);
    }
  }, [
    closeSceneModal,
    documentId,
    editingSceneDocumentId,
    editingSceneId,
    fetchDocumentScenes,
    fetchEntityRefs,
    projectId,
    sceneLinkContext,
    showErrorToast,
    t,
    token,
    unlinkingScene,
  ]);

  const handleChapterSceneDragEnd = useCallback(
    async (result: ChapterSceneDragEndResult): Promise<void> => {
      if (!documentId || (!token && !masterKey)) return;

      const { scenesByChapter: nextScenesByChapter, sceneId, fromChapterId, toChapterId } = result;
      const sceneDocumentId = result.sceneDocumentId ?? documentId;
      const fromUnlinked = isUnlinkedScenesContainer(fromChapterId);
      const toUnlinked = isUnlinkedScenesContainer(toChapterId);
      const membershipChanged = fromChapterId !== toChapterId;

      const applyMembershipToRefs = (
        prev: typeof entityRefs,
      ): typeof entityRefs => {
        let next = prev;
        if (!fromUnlinked) {
          next = next.filter((ref) => {
            const sceneToFrom =
              ref.sourceType === 'scene' &&
              ref.sourceId === sceneId &&
              ref.sourceDocumentId === sceneDocumentId &&
              ref.targetType === 'manual_chapter' &&
              ref.targetId === fromChapterId &&
              (ref.targetDocumentId === documentId || ref.targetDocumentId == null);
            const fromToScene =
              ref.sourceType === 'manual_chapter' &&
              ref.sourceId === fromChapterId &&
              ref.sourceDocumentId === documentId &&
              ref.targetType === 'scene' &&
              ref.targetId === sceneId &&
              (ref.targetDocumentId === sceneDocumentId || ref.targetDocumentId == null);
            return !sceneToFrom && !fromToScene;
          });
        }
        if (!toUnlinked) {
          const alreadyLinked = next.some(
            (ref) =>
              ref.sourceType === 'scene' &&
              ref.sourceId === sceneId &&
              ref.sourceDocumentId === sceneDocumentId &&
              ref.targetType === 'manual_chapter' &&
              ref.targetId === toChapterId &&
              (ref.targetDocumentId === documentId || ref.targetDocumentId == null),
          );
          if (!alreadyLinked) {
            next = [
              ...next,
              {
                sourceType: 'scene',
                sourceId: sceneId,
                sourceDocumentId: sceneDocumentId,
                targetType: 'manual_chapter',
                targetId: toChapterId,
                targetDocumentId: documentId,
              },
            ];
          }
        }
        return next;
      };

      try {
        if (membershipChanged) {
          if (!projectId || !token) {
            showErrorToast(t('failed_to_link_scene'));
            await fetchEntityRefs();
            return;
          }

          const nextRefs = applyMembershipToRefs(entityRefs);
          setEntityRefs(nextRefs);

          if (!fromUnlinked) {
            const unlinked = await removeSceneLinks({
              projectId,
              token,
              sceneId,
              documentId,
              sceneDocumentId,
              context: { type: 'chapter', id: fromChapterId },
            });
            if (!unlinked) {
              showErrorToast(t('failed_to_unlink_scene'));
              await fetchEntityRefs();
              return;
            }
          }

          if (!toUnlinked) {
            const linked = await createSceneLink({
              projectId,
              token,
              sceneId,
              documentId,
              sceneDocumentId,
              context: { type: 'chapter', id: toChapterId },
            });
            if (!linked) {
              showErrorToast(t('failed_to_link_scene'));
              await fetchEntityRefs();
              return;
            }
            const scene =
              projectSceneCatalog.find(
                (entry) => entry.id === sceneId && entry.documentId === sceneDocumentId,
              ) ??
              documentScenes.find((entry) => entry.id === sceneId) ?? {
                id: sceneId,
                title: sceneId,
                order: 0,
                documentId: sceneDocumentId,
              };
            maybePromptMoveScene(
              {
                id: scene.id,
                title: scene.title,
                documentTitle: scene.documentTitle ?? getDocumentTitle(sceneDocumentId),
              },
              sceneDocumentId,
              documentId,
              entityRefs,
              nextRefs,
            );
          }
        }

        if (fromUnlinked && toUnlinked) {
          return;
        }

        const allChapters: Chapter[] = [];
        Object.values(chaptersByLevel).forEach((levelChapters) => {
          allChapters.push(...levelChapters);
        });
        const manualChapters = allChapters.filter((chapter) => chapter.isManual && chapter.id);

        const orderedScenes = buildSceneOrderFromChapterLists(
          manualChapters,
          nextScenesByChapter,
          documentScenes,
          documentId,
        );
        const orderById = new Map(orderedScenes.map((scene) => [scene.id, scene.order]));

        for (const scene of documentScenes) {
          const newOrder = orderById.get(scene.id);
          if (newOrder == null || scene.order === newOrder) continue;
          const url = masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}`;
          const response = await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              order: newOrder,
              title: scene.title,
              description: scene.description,
            }),
          });
          if (!response.ok) throw new Error('Failed to update scene order');
        }

        setDocumentScenes(orderedScenes);
      } catch (err) {
        console.error('Error moving scene between chapters:', err);
        showErrorToast(t('failed_to_update_scene_order'));
        await fetchDocumentScenes();
        await fetchEntityRefs();
      }
    },
    [
      chaptersByLevel,
      documentId,
      documentScenes,
      fetchDocumentScenes,
      fetchEntityRefs,
      getDocumentTitle,
      masterKey,
      maybePromptMoveScene,
      projectId,
      projectSceneCatalog,
      showErrorToast,
      t,
      token,
    ],
  );

  const characters = useMemo(
    () => mentionableEntities.filter((e) => e.type === 'character'),
    [mentionableEntities]
  );

  const storylinesForChapters = useMemo(
    () =>
      mentionableEntities
        .filter((e) => e.type === 'storyline')
        .map((e) => ({ id: e.id, title: e.label })),
    [mentionableEntities]
  );

  const scenesForChapters = useMemo(
    () =>
      mentionableEntities
        .filter((e) => e.type === 'scene')
        .map((e) => ({ id: e.id, title: e.label })),
    [mentionableEntities]
  );

  const projectScenes = useMemo(() => {
    if (!documentId) return [] as Scene[];
    const byKey = new Map<string, Scene>();
    for (const scene of projectSceneCatalog) {
      if (!scene.documentId) continue;
      byKey.set(`${scene.documentId}:${scene.id}`, scene);
    }
    for (const scene of documentScenes) {
      byKey.set(`${documentId}:${scene.id}`, {
        ...scene,
        documentId: scene.documentId ?? documentId,
        documentTitle: scene.documentTitle ?? byKey.get(`${documentId}:${scene.id}`)?.documentTitle,
      });
    }
    return [...byKey.values()];
  }, [documentId, documentScenes, projectSceneCatalog]);

  const scenesByChapter = useMemo(() => {
    const map = new Map<string, Scene[]>();
    if (!documentId) return map;

    const scenesByKey = new Map(
      projectScenes.map((scene) => [`${sceneDocumentIdOf(scene, documentId)}:${scene.id}`, scene]),
    );

    for (const chapter of Object.values(chaptersByLevel).flat()) {
      if (!chapter.id || !chapter.isManual) continue;
      const linkedKeys = new Set<string>();
      for (const ref of entityRefs) {
        if (
          ref.sourceType === 'manual_chapter' &&
          ref.sourceId === chapter.id &&
          ref.sourceDocumentId === documentId &&
          ref.targetType === 'scene' &&
          ref.targetId
        ) {
          linkedKeys.add(`${ref.targetDocumentId ?? documentId}:${ref.targetId}`);
        }
        if (
          ref.targetType === 'manual_chapter' &&
          ref.targetId === chapter.id &&
          (ref.targetDocumentId === documentId || ref.targetDocumentId == null) &&
          ref.sourceType === 'scene' &&
          ref.sourceId
        ) {
          linkedKeys.add(`${ref.sourceDocumentId ?? documentId}:${ref.sourceId}`);
        }
      }
      const linkedScenes = [...linkedKeys]
        .map((key) => scenesByKey.get(key))
        .filter((scene): scene is Scene => Boolean(scene))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      map.set(chapter.id, linkedScenes);
    }
    return map;
  }, [chaptersByLevel, projectScenes, entityRefs, documentId]);

  const unlinkedScenes = useMemo(() => {
    if (!documentId) return [];
    return getUnlinkedChapterScenes(
      projectScenes.filter(
        (scene) => sceneDocumentIdOf(scene, documentId) === documentId,
      ),
      entityRefs,
      documentId,
    );
  }, [documentId, projectScenes, entityRefs]);

  const availableScenesTree = useMemo(() => {
    if (!documentId) {
      return { currentUnlinkedScenes: [], documents: [] };
    }
    return buildAvailableScenesTree({
      scenes: projectScenes,
      chapters: projectChapterCatalog,
      refs: entityRefs,
      currentDocumentId: documentId,
    });
  }, [documentId, projectScenes, projectChapterCatalog, entityRefs]);

  const linkableScenes = useMemo(() => {
    if (!documentId || !sceneLinkContext) return [];
    return getLinkableScenes(projectScenes, entityRefs, documentId, sceneLinkContext);
  }, [documentId, projectScenes, entityRefs, sceneLinkContext]);

  const sceneIsLinkedToContext = Boolean(
    editingSceneId &&
      documentId &&
      sceneLinkContext &&
      isSceneLinkedToContext(
        entityRefs,
        editingSceneId,
        editingSceneDocumentId ?? documentId,
        sceneLinkContext,
        documentId,
      ),
  );

  const sceneRelatedEntities = useMemo(() => {
    if (!editingSceneId || !documentId) return { linksTo: [], referencedIn: [] };
    return getRelatedEntitiesFor(
      'scene',
      editingSceneId,
      editingSceneDocumentId ?? documentId,
      entityRefs,
      mentionableEntities,
    );
  }, [editingSceneId, editingSceneDocumentId, documentId, entityRefs, mentionableEntities]);

  const handleRemoveSceneLinksTo = useCallback(
    async (entity: RelatedEntity): Promise<void> => {
      if (!editingSceneId || !documentId || !projectId || !token) return;
      const sceneDocumentId = editingSceneDocumentId ?? documentId;
      try {
        const removed = await removeSceneOutboundLink({
          projectId,
          token,
          sceneId: editingSceneId,
          documentId: sceneDocumentId,
          entity,
        });
        if (!removed) {
          showErrorToast(t('Failed to remove entity reference'));
          return;
        }
        const newDesc = removeMentionFromHtml(newSceneDescription || '', entity);
        if (newDesc !== (newSceneDescription || '')) {
          const order =
            (sceneDocumentId === documentId
              ? documentScenes.find((s) => s.id === editingSceneId)?.order
              : projectSceneCatalog.find((s) => s.id === editingSceneId && s.documentId === sceneDocumentId)?.order) ?? 1;
          const url = masterKey
            ? `${API_BASE}/api/documents/${sceneDocumentId}/scenes/${editingSceneId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${sceneDocumentId}/scenes/${editingSceneId}`;
          const res = await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              order,
              title: newSceneTitle.trim() || documentScenes.find((s) => s.id === editingSceneId)?.title,
              description: newDesc.trim() || undefined,
            }),
          });
          if (!res.ok) throw new Error('Failed to update');
          setNewSceneDescription(newDesc.trim() || '');
          await fetchDocumentScenes();
        }
        await fetchEntityRefs();
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [
      editingSceneId,
      editingSceneDocumentId,
      documentId,
      projectId,
      token,
      newSceneDescription,
      newSceneTitle,
      documentScenes,
      projectSceneCatalog,
      masterKey,
      fetchDocumentScenes,
      fetchEntityRefs,
      showErrorToast,
      t,
    ],
  );

  const handleRemoveSceneReferencedIn = useCallback(
    async (entity: RelatedEntity): Promise<void> => {
      if (!token || !projectId || !editingSceneId || !documentId) return;
      const sceneDocumentId = editingSceneDocumentId ?? documentId;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceType: entity.type,
            sourceId: entity.id,
            sourceDocumentId: entity.documentId ?? null,
            targetType: 'scene',
            targetId: editingSceneId,
            targetDocumentId: sceneDocumentId,
          }),
        });
        if (res.ok) {
          await fetchEntityRefs();
        } else {
          showErrorToast(t('Failed to remove entity reference'));
        }
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [editingSceneId, editingSceneDocumentId, documentId, projectId, token, fetchEntityRefs, t, showErrorToast],
  );

  const relatedEntities = useMemo(() => {
    if (!editingChapterId || !documentId) return { linksTo: [], referencedIn: [] };
    return getRelatedEntitiesFor(
      'manual_chapter',
      editingChapterId,
      documentId,
      entityRefs,
      mentionableEntities,
    );
  }, [editingChapterId, documentId, entityRefs, mentionableEntities]);

  const handleRemoveLinksTo = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!editingChapterId || !documentId) return;
      const newSummary = removeMentionFromHtml(newChapterSummary || '', entity);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/chapters/${editingChapterId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/chapters/${editingChapterId}`;
      try {
        const orderNum = parseInt(newChapterOrder.trim(), 10);
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            order: isNaN(orderNum) ? undefined : orderNum,
            title: newChapterTitle.trim(),
            wordsGoal: newChapterWordsGoal.trim() ? parseInt(newChapterWordsGoal, 10) : undefined,
            summary: newSummary.trim() || undefined,
          }),
        });
        if (!res.ok) throw new Error('Failed to update');
        setNewChapterSummary(newSummary.trim() || '');
        const chaptersUrl = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters`;
        const chaptersRes = await fetch(chaptersUrl, {
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (chaptersRes.ok) {
          const data = await chaptersRes.json();
          setChaptersByLevel(data.chaptersByLevel || {});
        }
        await fetchEntityRefs();
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [
      editingChapterId,
      documentId,
      newChapterSummary,
      newChapterOrder,
      newChapterTitle,
      newChapterWordsGoal,
      masterKey,
      token,
      fetchEntityRefs,
      t,
      showErrorToast,
    ]
  );

  const handleRemoveReferencedIn = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!token || !projectId || !editingChapterId || !documentId) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceType: entity.type,
            sourceId: entity.id,
            sourceDocumentId: entity.documentId ?? null,
            targetType: 'manual_chapter',
            targetId: editingChapterId,
            targetDocumentId: documentId,
          }),
        });
        if (res.ok) {
          await fetchEntityRefs();
        } else {
          showErrorToast(t('Failed to remove entity reference'));
        }
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [editingChapterId, documentId, projectId, token, fetchEntityRefs, t, showErrorToast]
  );

  const sensors = useSortableSensors();

  const closeChapterModal = useCallback((): void => {
    setShowAddModal(false);
    setEditingChapterId(null);
    setNewChapterOrder('');
    setNewChapterTitle('');
    setNewChapterWordsGoal('');
    setNewChapterSummary('');
  }, []);

  const handleDeleteChapter = useCallback(
    async (chapter: Chapter): Promise<void> => {
      if (!chapter.id || deletingChapterId === chapter.id) return;

      try {
        setDeletingChapterId(chapter.id);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}`;
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete chapter');
        }

        if (editingChapterId === chapter.id) {
          closeChapterModal();
        }

        if ((!token && !masterKey) || !documentId) {
          return;
        }
        try {
          setLoading(true);
          setError(null);
          const chaptersUrl = masterKey
            ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/chapters`;
          const chaptersResponse = await fetch(chaptersUrl, {
            headers: {
              ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          if (!chaptersResponse.ok) {
            throw new Error('Failed to fetch chapters');
          }

          const data = await chaptersResponse.json();
          setChaptersByLevel(data.chaptersByLevel || {});
        } catch (err) {
          console.error('Error fetching chapters:', err);
          setError(t('failed_to_load_chapters'));
        } finally {
          setLoading(false);
        }

        await fetchEntityRefs();
      } catch (err) {
        console.error('Error deleting chapter:', err);
        setError(t('failed_to_delete_chapter'));
      } finally {
        setDeletingChapterId(null);
      }
    },
    [
      closeChapterModal,
      deletingChapterId,
      documentId,
      editingChapterId,
      fetchEntityRefs,
      masterKey,
      t,
      token,
    ],
  );

  useEffect(() => {
    if (!showAddModal || editingChapterId !== null) {
      return;
    }

    const allChapters: Chapter[] = [];
    Object.values(chaptersByLevel).forEach((chapters) => {
      allChapters.push(...chapters);
    });

    const manualChapters = allChapters.filter((ch) => ch.isManual);
    const maxOrder = manualChapters.length > 0
      ? Math.max(...manualChapters.map((ch) => ch.order || 0))
      : 0;
    const nextOrder = maxOrder + 1;

    setNewChapterOrder(nextOrder.toString());
  }, [showAddModal, editingChapterId, chaptersByLevel]);

  useEffect(() => {
    const fetchChapters = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch chapters');
        }

        const data = await response.json();
        setChaptersByLevel(data.chaptersByLevel || {});
      } catch (err) {
        console.error('Error fetching chapters:', err);
        setError(t('failed_to_load_chapters'));
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [token, documentId, masterKey]);

  useEffect(() => {
    if (!initialChapterId || loading) return;
    if (mode === 'analysis') {
      setPendingScrollChapterId(initialChapterId);
      return;
    }
    const allChapters: Chapter[] = [];
    Object.values(chaptersByLevel).forEach((chapters) => allChapters.push(...chapters));
    const chapter = allChapters.find((ch) => ch.id === initialChapterId);
    if (chapter) {
      setEditingChapterId(initialChapterId);
      setNewChapterOrder(chapter.order?.toString() || '');
      setNewChapterTitle(chapter.title || '');
      setNewChapterWordsGoal(chapter.wordsGoal?.toString() || '');
      setNewChapterSummary(chapter.summary || '');
      setShowAddModal(true);
    }
  }, [initialChapterId, loading, chaptersByLevel, mode]);

  useEffect(() => {
    if (!pendingScrollChapterId || loading) return;

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const tryScroll = (): boolean => {
      const el = document.querySelector(
        `[data-chapter-id="${pendingScrollChapterId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
      );
      if (!el || typeof (el as HTMLElement).scrollIntoView !== 'function') return false;
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      if (tryScroll()) {
        setPendingScrollChapterId(null);
        return;
      }
      retryTimeout = setTimeout(() => {
        if (cancelled) return;
        tryScroll();
        setPendingScrollChapterId(null);
      }, 150);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [pendingScrollChapterId, chaptersByLevel, loading, viewMode]);

  useEffect(() => {
    const fetchDocumentAnalysis = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId) {
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/analyze?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/analyze`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDocumentAnalysis(data.analysis || null);
        }
      } catch (err) {
        console.error('Error fetching document analysis:', err);
      }
    };

    fetchDocumentAnalysis();
  }, [token, documentId, masterKey]);

  const handleAnalyzeChapter = async (chapterTitle: string): Promise<void> => {
    if (!documentId || (!token && !masterKey && !shareToken)) {
      return;
    }

    setAnalyzingChapters((prev) => new Set(prev).add(chapterTitle));

    const abortController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      const encodedTitle = encodeURIComponent(chapterTitle);
      const authToken = shareToken || token;
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/chapters/${encodedTitle}/analyze?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/chapters/${encodedTitle}/analyze`;

      timeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          abortController.abort();
        }
      }, 120000);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(authToken && !masterKey ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        signal: abortController.signal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to analyze chapter' }));
        throw new Error(errorData.error || 'Failed to analyze chapter');
      }

      const refreshChapters = async (): Promise<void> => {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setChaptersByLevel(data.chaptersByLevel || {});
        }
      };

      await refreshChapters();
      setExpandedAnalyses((prev) => new Set(prev).add(chapterTitle));
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (err instanceof Error && err.name === 'AbortError') {
        setError('El análisis tardó demasiado. Por favor, intenta de nuevo.');
      } else {
        console.error('Error analyzing chapter:', err);
        setError(err instanceof Error ? err.message : t('failed_to_analyze_chapter'));
      }
    } finally {
      setAnalyzingChapters((prev) => {
        const next = new Set(prev);
        next.delete(chapterTitle);
        return next;
      });
    }
  };

  const toggleAnalysisExpanded = (chapterTitle: string): void => {
    setExpandedAnalyses((prev) => {
      const next = new Set(prev);
      if (next.has(chapterTitle)) {
        next.delete(chapterTitle);
      } else {
        next.add(chapterTitle);
      }
      return next;
    });
  };

  const [savingAnalysisField, setSavingAnalysisField] = useState(false);

  const persistChapterAnalysisField = useCallback(
    async (
      chapterTitle: string,
      field: EditableChapterAnalysisField,
      value: string | number | string[],
    ): Promise<boolean> => {
      if (!documentId || (!token && !masterKey) || shareToken || savingAnalysisField) {
        return false;
      }

      let previousAnalysis: ChapterAnalysisData | null = null;
      for (const chapters of Object.values(chaptersByLevel) as Chapter[][]) {
        const chapter = chapters.find((ch: Chapter) => ch.title === chapterTitle);
        if (chapter?.analysis) {
          previousAnalysis = chapter.analysis;
          break;
        }
      }

      if (!previousAnalysis) {
        return false;
      }

      if (JSON.stringify(previousAnalysis[field]) === JSON.stringify(value)) {
        return false;
      }

      const nextAnalysis: ChapterAnalysisData = {
        ...previousAnalysis,
        [field]: value,
      };

      setChaptersByLevel((prev) => {
        const next: ChaptersByLevel = {};
        for (const [levelKey, chapters] of Object.entries(prev)) {
          next[Number(levelKey)] = chapters.map((ch: Chapter) =>
            ch.title === chapterTitle && ch.analysis
              ? { ...ch, analysis: nextAnalysis }
              : ch,
          );
        }
        return next;
      });
      setSavingAnalysisField(true);

      try {
        const encodedTitle = encodeURIComponent(chapterTitle);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters/${encodedTitle}/analysis?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters/${encodedTitle}/analysis`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ [field]: value }),
        });

        if (!response.ok) {
          throw new Error('Failed to update chapter analysis');
        }

        const data = await response.json();
        const saved = data.analysis as ChapterAnalysisData;
        setChaptersByLevel((prev) => {
          const next: ChaptersByLevel = {};
          for (const [levelKey, chapters] of Object.entries(prev)) {
            next[Number(levelKey)] = chapters.map((ch: Chapter) =>
              ch.title === chapterTitle ? { ...ch, analysis: saved } : ch,
            );
          }
          return next;
        });
        return true;
      } catch (err) {
        setChaptersByLevel((prev) => {
          const next: ChaptersByLevel = {};
          for (const [levelKey, chapters] of Object.entries(prev)) {
            next[Number(levelKey)] = chapters.map((ch: Chapter) =>
              ch.title === chapterTitle && previousAnalysis
                ? { ...ch, analysis: previousAnalysis }
                : ch,
            );
          }
          return next;
        });
        console.error('Error updating chapter analysis field:', err);
        showErrorToast(t('failed_to_update_chapter_analysis'));
        return false;
      } finally {
        setSavingAnalysisField(false);
      }
    },
    [
      documentId,
      token,
      masterKey,
      shareToken,
      savingAnalysisField,
      chaptersByLevel,
      showErrorToast,
      t,
    ],
  );

  const handleAddToOutline = useCallback(
    async (chapter: { title: string; summary?: string }): Promise<void> => {
      if (!documentId || (!token && !masterKey)) return;
      setAddingToOutlineChapters((prev) => new Set(prev).add(chapter.title));
      try {
        const allChapters: Chapter[] = [];
        Object.values(chaptersByLevel).forEach((chapters) => allChapters.push(...chapters));
        const manualChapters = allChapters.filter((ch) => ch.isManual);
        const maxOrder =
          manualChapters.length > 0 ? Math.max(...manualChapters.map((ch) => ch.order ?? 0)) : 0;
        const order = maxOrder + 1;
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            order,
            title: chapter.title.trim(),
            summary: chapter.summary?.trim() || undefined,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error ?? 'Failed to add chapter');
        }
        const createdData = await response.json().catch(() => null);
        const createdChapterId =
          createdData && typeof createdData === 'object' && createdData.chapter?.id
            ? String(createdData.chapter.id)
            : null;
        const chaptersUrl = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters`;
        const chaptersRes = await fetch(chaptersUrl, {
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (chaptersRes.ok) {
          const data = await chaptersRes.json();
          setChaptersByLevel(data.chaptersByLevel || {});
        }
        if (createdChapterId) {
          setPendingScrollChapterId(createdChapterId);
        }
        await fetchEntityRefs();
      } catch (err) {
        console.error('Error adding to outline:', err);
        showErrorToast(err instanceof Error ? err.message : t('failed_to_create_chapter'));
      } finally {
        setAddingToOutlineChapters((prev) => {
          const next = new Set(prev);
          next.delete(chapter.title);
          return next;
        });
      }
    },
    [documentId, token, masterKey, chaptersByLevel, fetchEntityRefs, showErrorToast, t]
  );

  const handleChapterTitleChange = useCallback(
    async (chapter: Chapter, title: string): Promise<boolean> => {
      if (!chapter.id || !documentId || (!token && !masterKey)) return false;
      const trimmed = title.trim();
      if (!trimmed) return false;
      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ title: trimmed }),
        });
        if (!response.ok) throw new Error('Failed to update chapter title');
        setChaptersByLevel((prev) => {
          const next: ChaptersByLevel = {};
          for (const [level, chapters] of Object.entries(prev)) {
            next[Number(level)] = chapters.map((ch: Chapter) =>
              ch.id === chapter.id ? { ...ch, title: trimmed } : ch,
            );
          }
          return next;
        });
        return true;
      } catch (err) {
        console.error('Error updating chapter title:', err);
        showErrorToast(t('failed_to_update_chapter'));
        return false;
      }
    },
    [documentId, token, masterKey, showErrorToast, t],
  );

  const handleChapterSummaryChange = useCallback(
    async (chapter: Chapter, summary: string): Promise<void> => {
      if (!chapter.id || !documentId || (!token && !masterKey)) return;
      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ summary }),
        });
        if (!response.ok) throw new Error('Failed to update chapter summary');
        setChaptersByLevel((prev) => {
          const next: ChaptersByLevel = {};
          for (const [level, chapters] of Object.entries(prev)) {
            next[Number(level)] = chapters.map((ch: Chapter) =>
              ch.id === chapter.id ? { ...ch, summary: summary || undefined } : ch,
            );
          }
          return next;
        });
        await fetchEntityRefs();
      } catch (err) {
        console.error('Error updating chapter summary:', err);
        showErrorToast(t('failed_to_update_chapter'));
      }
    },
    [documentId, token, masterKey, fetchEntityRefs, showErrorToast, t],
  );

  const getDisplayTitle = (title: string): string => {
    if (title === 'Document') {
      return t('document_chapter_title');
    }
    return title;
  };

  const getSectionHref = useCallback((title: string, level: number): string => {
    if (!documentId) {
      return '#';
    }
    return buildDocumentSectionPath(
      projectId,
      documentId,
      { title, level },
      window.location.search,
    );
  }, [documentId, projectId]);

  const handleScrollToSection = useCallback((title: string, level: number): void => {
    if (onScrollToSectionProp) {
      onScrollToSectionProp(title, level);
      return;
    }
    if (!documentId) {
      return;
    }
    navigate(getSectionHref(title, level));
  }, [documentId, getSectionHref, navigate, onScrollToSectionProp]);

  const { hasExtractedChapters, hasManualChapters } = useMemo(() => {
    const allChapters: Chapter[] = [];
    Object.values(chaptersByLevel).forEach((chapters) => {
      allChapters.push(...chapters);
    });
    return {
      hasExtractedChapters: allChapters.some((ch) => !ch.isManual),
      hasManualChapters: allChapters.some((ch) => ch.isManual),
    };
  }, [chaptersByLevel]);

  const handleAnalyzeFullDocument = async (): Promise<void> => {
    if (!documentId || (!token && !masterKey)) {
      return;
    }

    setAnalyzingDocument(true);

    const abortController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/analyze?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/analyze`;

      timeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          abortController.abort();
        }
      }, 300000);

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: abortController.signal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to analyze document' }));
        throw new Error(errorData.error || 'Failed to analyze document');
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.substring(6));
            if (data.type === 'keep-alive') {
              continue;
            } else if (data.type === 'complete') {
              setDocumentAnalysis(data.analysis);
              notifyOnboardingUpdate(data.onboarding);
            } else if (data.type === 'error') {
              throw new Error(data.error || 'Failed to analyze document');
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError);
          }
        }
      }

      const refreshChapters = async (): Promise<void> => {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setChaptersByLevel(data.chaptersByLevel || {});
        }
      };

      await refreshChapters();
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (err instanceof Error && err.name === 'AbortError') {
        setError('El análisis tardó demasiado. Por favor, intenta de nuevo.');
      } else {
        console.error('Error analyzing document:', err);
        setError(err instanceof Error ? err.message : t('failed_to_analyze_document'));
      }
    } finally {
      setAnalyzingDocument(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;

    if (!over || active.id === over.id || !documentId || (!token && !masterKey)) {
      return;
    }

    const allChapters: Chapter[] = [];
    Object.values(chaptersByLevel).forEach((chapters) => {
      allChapters.push(...chapters);
    });

    const manualChapters = allChapters.filter((ch) => ch.isManual);
    const sortedChapters = [...manualChapters].sort((a, b) => (a.order || 0) - (b.order || 0));

    const oldIndex = sortedChapters.findIndex((ch) => (ch.id || `chapter-${sortedChapters.indexOf(ch)}`) === active.id);
    const newIndex = sortedChapters.findIndex((ch) => (ch.id || `chapter-${sortedChapters.indexOf(ch)}`) === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedChapters = arrayMove(sortedChapters, oldIndex, newIndex);

    try {
      const updatePromises = reorderedChapters.map(async (chapter, index) => {
        const newOrder = index + 1;
        if (chapter.order === newOrder || !chapter.id) {
          return;
        }

        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/chapters/${chapter.id}`;

        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            order: newOrder,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to update chapter order');
        }
      });

      await Promise.all(updatePromises);

      const orderById = new Map<string, number>();
      reorderedChapters.forEach((ch, i) => {
        if (ch.id) {
          orderById.set(ch.id, i + 1);
        }
      });
      setChaptersByLevel((prev) => {
        const next: ChaptersByLevel = {};
        for (const levelStr of Object.keys(prev)) {
          const level = Number(levelStr);
          next[level] = prev[level].map((ch) => {
            if (ch.id && orderById.has(ch.id)) {
              return { ...ch, order: orderById.get(ch.id)! };
            }
            return ch;
          });
        }
        return next;
      });
    } catch (err) {
      console.error('Error reordering chapters:', err);
      setError(t('failed_to_update_chapter_order'));
    }
  };

  if (loading) {
    if (isSidebar) {
      return (
        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
          {t('loading')}
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>{t('loading')}</div>
    );
  }

  if (error) {
    if (isSidebar) {
      return (
        <div className="flex flex-col gap-2 p-4">
          <div className="text-sm text-red-600">{error}</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const fetchChapters = async (): Promise<void> => {
                if ((!token && !masterKey) || !documentId) {
                  return;
                }
                try {
                  setLoading(true);
                  setError(null);
                  const url = masterKey
                    ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
                    : `${API_BASE}/api/documents/${documentId}/chapters`;
                  const response = await fetch(url, {
                    headers: {
                      ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
                    },
                  });
                  if (!response.ok) {
                    throw new Error('Failed to fetch chapters');
                  }
                  const data = await response.json();
                  setChaptersByLevel(data.chaptersByLevel || {});
                } catch (err) {
                  console.error('Error fetching chapters:', err);
                  setError(t('failed_to_load_chapters'));
                } finally {
                  setLoading(false);
                }
              };
              fetchChapters();
            }}
            className="w-full"
          >
            {t('retry')}
          </Button>
        </div>
      );
    }
    return (
      <div>
        <PanelPageHeader
          onClose={onClose}
          closeAriaLabel={t('close')}
        />
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '3rem' }}>{error}</div>
      </div>
    );
  }

  const addChapterButton = (
    <AddItemButton
      onClick={() => setShowAddModal(true)}
      label={t('add_chapter')}
    />
  );
  const analyzeFullDocumentButton = (
    <AnalyzeButton
      className="mr-0"
      onClick={handleAnalyzeFullDocument}
      isAnalyzing={analyzingDocument}
    >
      {analyzingDocument ? t('analyzing_document') : t('analyze_full_document')}
    </AnalyzeButton>
  );
  const chaptersViewSwitcher = (
    <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as typeof viewMode)}>
      <ViewSwitcher
        modes={['list', 'grid', 'table', 'timeline']}
        ariaLabelForMode={(modeName) => (modeName === 'timeline' ? t('Timeline') : t(modeName))}
      />
    </Tabs>
  );

  const chaptersContent = (
    <div className={`${PANEL_BODY_FILL_CLASSNAME} min-h-0 overflow-hidden`}>
        {mode === 'outline' ? (
          <PanelHeaderActions
            isSidebar={isSidebar}
            onClose={onClose}
            closeAriaLabel={t('close')}
            hasItems={hasManualChapters}
            viewSwitcher={chaptersViewSwitcher}
          >
            {addChapterButton}
          </PanelHeaderActions>
        ) : (
          <PanelHeaderActions
            isSidebar={isSidebar}
            onClose={onClose}
            closeAriaLabel={t('close')}
            hasItems={hasExtractedChapters}
          >
            {analyzeFullDocumentButton}
          </PanelHeaderActions>
        )}

        <div className={`flex min-h-0 flex-1 flex-col ${mode === 'outline' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        {(() => {
          const allChapters: Chapter[] = [];
          Object.values(chaptersByLevel).forEach((chapters) => {
            allChapters.push(...chapters);
          });

          const manualChapters = allChapters.filter((ch) => ch.isManual);
          const extractedChapters = allChapters.filter((ch) => !ch.isManual);

          if (mode === 'outline') {
            if (manualChapters.length === 0) {
              return (
                <div className={`min-h-0 flex-1 overflow-y-auto ${isSidebar ? 'p-4' : 'p-8'}`}>
                  <ChaptersEmptyState canEdit onAdd={() => setShowAddModal(true)} />
                </div>
              );
            }
            return (
              <ChaptersOutline
                chapters={manualChapters}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isSidebar={isSidebar}
                onDragEnd={handleDragEnd}
                sensors={sensors}
                onAddChapter={() => setShowAddModal(true)}
                scenesByChapter={scenesByChapter}
                unlinkedScenes={unlinkedScenes}
                availableScenesTree={availableScenesTree}
                mentionableEntities={mentionableEntities}
                onAddScene={openAddSceneModal}
                onTitleChange={handleChapterTitleChange}
                onSummaryChange={handleChapterSummaryChange}
                onEditScene={openEditSceneModal}
                onSceneDragEnd={handleChapterSceneDragEnd}
                chapterTimelineGroupBy={chapterTimelineGroupBy}
                onChapterTimelineGroupByChange={(groupMode) => {
                  setChapterTimelineGroupBy(groupMode);
                  if (groupMode === 'character') {
                    setChapterTimelineGroupByCharacters(new Set(characters.map((c) => c.id)));
                  }
                  if (groupMode === 'storyline') {
                    setChapterTimelineGroupByStorylines(new Set(storylinesForChapters.map((s) => s.id)));
                  }
                  if (groupMode === 'scene') {
                    setChapterTimelineGroupByScenes(new Set(scenesForChapters.map((s) => s.id)));
                  }
                }}
                chapterTimelineGroupByCharacters={chapterTimelineGroupByCharacters}
                onChapterTimelineGroupByCharactersChange={setChapterTimelineGroupByCharacters}
                chapterTimelineGroupByStorylines={chapterTimelineGroupByStorylines}
                onChapterTimelineGroupByStorylinesChange={setChapterTimelineGroupByStorylines}
                chapterTimelineGroupByScenes={chapterTimelineGroupByScenes}
                onChapterTimelineGroupByScenesChange={setChapterTimelineGroupByScenes}
                entityRefs={entityRefs}
                characters={characters.map((c: MentionableEntity) => ({ id: c.id, name: c.label }))}
                storylines={storylinesForChapters}
                scenes={scenesForChapters}
                onEdit={(ch) => {
                  if (!ch.id || editingChapterId === ch.id) return;
                  setEditingChapterId(ch.id);
                  setNewChapterOrder(ch.order?.toString() || '');
                  setNewChapterTitle(ch.title || '');
                  setNewChapterWordsGoal(ch.wordsGoal?.toString() || '');
                  setNewChapterSummary(ch.summary || '');
                  setShowAddModal(true);
                }}
                onDelete={(ch) => {
                  if (!ch.id || deletingChapterId === ch.id) return;
                  setDeleteConfirmChapter(ch);
                }}
                deletingChapterId={deletingChapterId}
                editingChapterId={editingChapterId}
                token={token}
                masterKey={masterKey}
                documentId={documentId}
                t={t}
              />
            );
          }

          if (extractedChapters.length === 0 && !documentAnalysis) {
            return (
              <ChaptersEmptyState
                title={t('analysis_empty_title')}
                description={t('analysis_empty_description')}
              />
            );
          }

          return (
            <>
              {extractedChapters.length > 0 && (
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    margin: '0 0 1rem 0',
                  }}
                >
                  {t('document_chapters')}
                </h2>
              )}

              <DocumentChapters
                chaptersByLevel={chaptersByLevel}
                isSidebar={isSidebar}
                analyzingChapters={analyzingChapters}
                expandedAnalyses={expandedAnalyses}
                onAnalyzeAll={handleAnalyzeFullDocument}
                analyzingDocument={analyzingDocument}
                showAnalyzeAllButton={false}
                onAnalyzeChapter={handleAnalyzeChapter}
                onToggleAnalysisExpanded={toggleAnalysisExpanded}
                onAddToOutline={handleAddToOutline}
                addingToOutlineChapters={addingToOutlineChapters}
                getDisplayTitle={getDisplayTitle}
                getSectionHref={getSectionHref}
                onScrollToSection={handleScrollToSection}
                canEditAnalysis={!shareToken}
                savingAnalysisField={savingAnalysisField}
                onPersistChapterAnalysisField={persistChapterAnalysisField}
                t={t}
              />

              {documentAnalysis && (documentAnalysis.mainPlot || (documentAnalysis.subplots && documentAnalysis.subplots.length > 0)) && (
                <div style={{ marginTop: '2rem' }}>
                  {documentAnalysis.mainPlot && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h2
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          margin: '0 0 1rem 0',
                        }}
                      >
                        {t('main_plot')}
                      </h2>
                      <div
                        style={{
                          backgroundColor: 'var(--card)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          padding: '1.5rem',
                        }}
                      >
                        <p style={{ fontSize: '0.875rem', color: 'var(--foreground)', lineHeight: '1.6', margin: 0 }}>
                          {documentAnalysis.mainPlot}
                        </p>
                      </div>
                    </div>
                  )}

                  {documentAnalysis.subplots && documentAnalysis.subplots.length > 0 && (
                    <div>
                      <h2
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          margin: '0 0 1rem 0',
                        }}
                      >
                        {t('subplots')}
                      </h2>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {documentAnalysis.subplots.map((subplot: any, index: number) => (
                          <div
                            key={index}
                            style={{
                              backgroundColor: 'var(--card)',
                              border: '1px solid var(--border)',
                              borderRadius: '8px',
                              padding: '1.5rem',
                            }}
                          >
                            <div style={{ marginBottom: '0.75rem' }}>
                              <p style={{ fontSize: '0.875rem', color: 'var(--foreground)', lineHeight: '1.6', margin: 0 }}>
                                {subplot.description}
                              </p>
                            </div>
                            {subplot.relationshipToMainPlot && (
                              <div style={{ marginBottom: '0.75rem' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--muted-foreground)', marginBottom: '0.25rem' }}>
                                  {t('relationship_to_main_plot')}
                                </div>
                                <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)', lineHeight: '1.5', margin: 0 }}>
                                  {subplot.relationshipToMainPlot}
                                </p>
                              </div>
                            )}
                            {subplot.keyChapters && subplot.keyChapters.length > 0 && (
                              <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--muted-foreground)', marginBottom: '0.25rem' }}>
                                  {t('key_chapters')}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
                                  {subplot.keyChapters.join(', ')}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {documentAnalysis && documentAnalysis.questions && documentAnalysis.questions.length > 0 && (
                <QuestionsTable
                  questions={documentAnalysis.questions}
                  isSidebar={isSidebar}
                  t={t}
                />
              )}
            </>
          );
        })()}
        </div>
    </div>
  );

  const addChapterModal = (
    <Dialog open={showAddModal} onOpenChange={(open) => !open && !addingChapter && closeChapterModal()}>
      <DialogContent className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}>
        <DialogTitle className="sr-only">
          {editingChapterId ? t('edit_chapter') : t('add_chapter')}
        </DialogTitle>
        <EntityDetailModalLayout
          header={
            <EntityDetailModalTitle>
              {editingChapterId ? t('edit_chapter') : t('add_chapter')}
            </EntityDetailModalTitle>
          }
          footer={
            <EditDialogFooter
              isEditing={editingChapterId !== null}
              isSaving={addingChapter}
              saveDisabled={(() => {
                const orderNum = parseInt(newChapterOrder.trim(), 10);
                return !newChapterOrder.trim() || isNaN(orderNum) || orderNum <= 0 || !newChapterTitle.trim();
              })()}
              onCancel={closeChapterModal}
              onSave={() => {
                void (async () => {
                  const orderNum = parseInt(newChapterOrder.trim(), 10);
                  if (!newChapterOrder.trim() || isNaN(orderNum) || orderNum <= 0 || !newChapterTitle.trim() || addingChapter) return;

                  const isEditing = editingChapterId !== null;
                  try {
                    setAddingChapter(true);
                    const url = isEditing
                      ? (masterKey
                          ? `${API_BASE}/api/documents/${documentId}/chapters/${editingChapterId}?masterKey=${encodeURIComponent(masterKey)}`
                          : `${API_BASE}/api/documents/${documentId}/chapters/${editingChapterId}`)
                      : (masterKey
                          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
                          : `${API_BASE}/api/documents/${documentId}/chapters`);
                    const response = await fetch(url, {
                      method: isEditing ? 'PUT' : 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({
                        order: parseInt(newChapterOrder.trim(), 10),
                        title: newChapterTitle.trim(),
                        wordsGoal: newChapterWordsGoal.trim() ? parseInt(newChapterWordsGoal, 10) : undefined,
                        summary: (newChapterSummary || '').trim() || undefined,
                      }),
                    });

                    if (!response.ok) {
                      throw new Error(isEditing ? 'Failed to update chapter' : 'Failed to create chapter');
                    }

                    const createdData = !isEditing ? await response.json().catch(() => null) : null;
                    const createdChapterId =
                      createdData && typeof createdData === 'object' && createdData.chapter?.id
                        ? String(createdData.chapter.id)
                        : null;

                    const fetchChapters = async (): Promise<void> => {
                      if ((!token && !masterKey) || !documentId) {
                        return;
                      }
                      try {
                        setLoading(true);
                        setError(null);
                        const chaptersUrl = masterKey
                          ? `${API_BASE}/api/documents/${documentId}/chapters?masterKey=${encodeURIComponent(masterKey)}`
                          : `${API_BASE}/api/documents/${documentId}/chapters`;
                        const chaptersResponse = await fetch(chaptersUrl, {
                          headers: {
                            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
                          },
                        });

                        if (!chaptersResponse.ok) {
                          throw new Error('Failed to fetch chapters');
                        }

                        const data = await chaptersResponse.json();
                        setChaptersByLevel(data.chaptersByLevel || {});
                      } catch (err) {
                        console.error('Error fetching chapters:', err);
                        setError(t('failed_to_load_chapters'));
                      } finally {
                        setLoading(false);
                      }
                    };

                    await fetchChapters();
                    await fetchEntityRefs();
                    if (createdChapterId) {
                      setPendingScrollChapterId(createdChapterId);
                    }
                    closeChapterModal();
                  } catch (err) {
                    console.error(isEditing ? 'Error updating chapter:' : 'Error creating chapter:', err);
                    setError(isEditing ? t('failed_to_update_chapter') : t('failed_to_create_chapter'));
                  } finally {
                    setAddingChapter(false);
                  }
                })();
              }}
              onDelete={
                editingChapterId
                  ? () => {
                      const allChapters: Chapter[] = [];
                      Object.values(chaptersByLevel).forEach((chapters) => allChapters.push(...chapters));
                      const chapter = allChapters.find((ch) => ch.id === editingChapterId);
                      if (chapter) setDeleteConfirmChapter(chapter);
                    }
                  : undefined
              }
              deleteDisabled={deletingChapterId === editingChapterId}
            />
          }
        >
          <div className="space-y-6 p-4">
            <div className="flex gap-3">
              <div className="w-24 flex-shrink-0">
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  {t('chapter_order')} *
                </label>
                <input
                  type="number"
                  min="1"
                  value={newChapterOrder}
                  onChange={(e) => setNewChapterOrder(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-base text-foreground"
                  placeholder={t('chapter_order_placeholder')}
                  autoFocus
                />
              </div>
              <div className="min-w-0 flex-1">
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  {t('title')} *
                </label>
                <input
                  type="text"
                  value={newChapterTitle}
                  onChange={(e) => setNewChapterTitle(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-base text-foreground"
                  placeholder={t('chapter_title_placeholder')}
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t('words_goal')}
              </label>
              <input
                type="number"
                value={newChapterWordsGoal}
                onChange={(e) => setNewChapterWordsGoal(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-3 text-base text-foreground"
                placeholder={t('words_goal_placeholder')}
                min="1"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t('summary')}
              </label>
              <RichTextField
                value={newChapterSummary}
                onChange={setNewChapterSummary}
                placeholder={t('chapter_summary_placeholder')}
                minHeight="150px"
                mentionableEntities={mentionableEntities}
              />
            </div>
            {projectId && editingChapterId && (relatedEntities.linksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
              <div>
                <label className="mb-2 block text-sm font-semibold">
                  {t('related_entities')}
                </label>
                <div className="flex flex-col gap-3">
                  {relatedEntities.linksTo.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs text-muted-foreground">
                        {t('links_to')}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {relatedEntities.linksTo.map((e) => (
                          <RelatedEntityLink
                            key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                            entity={e}
                            projectId={projectId}
                            onRemove={() => handleRemoveLinksTo(e)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {relatedEntities.referencedIn.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs text-muted-foreground">
                        {t('referenced_in')}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {relatedEntities.referencedIn.map((e) => (
                          <RelatedEntityLink
                            key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                            entity={e}
                            projectId={projectId}
                            onRemove={() => handleRemoveReferencedIn(e)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </EntityDetailModalLayout>
      </DialogContent>
    </Dialog>
  );

  const sceneModal = (
    <SceneFormDialog
      open={showSceneModal}
      isEditing={editingSceneId !== null}
      isSaving={savingScene}
      title={newSceneTitle}
      description={newSceneDescription}
      mentionableEntities={mentionableEntities}
      linkableScenes={linkableScenes}
      projectId={projectId}
      relatedLinksTo={sceneRelatedEntities.linksTo}
      relatedReferencedIn={sceneRelatedEntities.referencedIn}
      deleteDisabled={deletingSceneId === editingSceneId}
      unlinkDisabled={unlinkingScene}
      showUnlink={sceneIsLinkedToContext}
      onTitleChange={setNewSceneTitle}
      onDescriptionChange={setNewSceneDescription}
      onSelectExistingScene={handleSelectExistingScene}
      onSave={() => {
        void handleSaveScene();
      }}
      onCancel={closeSceneModal}
      onDelete={
        editingSceneId
          ? () => {
              const scene =
                documentScenes.find((s) => s.id === editingSceneId) ??
                projectSceneCatalog.find(
                  (s) =>
                    s.id === editingSceneId &&
                    s.documentId === (editingSceneDocumentId ?? documentId),
                ) ??
                (editingSceneId
                  ? {
                      id: editingSceneId,
                      order: 0,
                      title: newSceneTitle,
                      documentId: editingSceneDocumentId ?? documentId ?? undefined,
                    }
                  : null);
              if (scene) setDeleteConfirmScene(scene);
            }
          : undefined
      }
      onUnlink={() => {
        void handleUnlinkScene();
      }}
      onRemoveLinksTo={(entity) => {
        void handleRemoveSceneLinksTo(entity);
      }}
      onRemoveReferencedIn={(entity) => {
        void handleRemoveSceneReferencedIn(entity);
      }}
    />
  );

  const deleteChapterDialog = (
    <ConfirmDialog
      open={deleteConfirmChapter !== null}
      onOpenChange={(open) => !open && setDeleteConfirmChapter(null)}
      title={t('delete_chapter')}
      description={t('confirm_delete_chapter')}
      confirmLabel={t('delete_chapter')}
      onConfirm={() => {
        if (deleteConfirmChapter) void handleDeleteChapter(deleteConfirmChapter);
      }}
    />
  );

  const deleteSceneDialog = (
    <ConfirmDialog
      open={deleteConfirmScene !== null}
      onOpenChange={(open) => !open && setDeleteConfirmScene(null)}
      title={t('delete_scene')}
      description={t('confirm_delete_scene')}
      confirmLabel={t('delete_scene')}
      onConfirm={() => {
        if (deleteConfirmScene) void handleDeleteScene(deleteConfirmScene);
      }}
    />
  );

  const moveSceneDialog = (
    <ConfirmDialog
      open={moveScenePrompt !== null}
      onOpenChange={(open) => !open && !movingScene && setMoveScenePrompt(null)}
      title={t('move_scene_title')}
      description={
        moveScenePrompt
          ? t('move_scene_description')
              .replace('{scene}', moveScenePrompt.sceneTitle)
              .replace('{fromDocument}', moveScenePrompt.fromDocumentTitle)
              .replace('{toDocument}', moveScenePrompt.toDocumentTitle)
          : ''
      }
      confirmLabel={t('move_scene')}
      cancelLabel={t('keep_scene_where_it_is')}
      destructive={false}
      onConfirm={() => {
        void handleConfirmMoveScene();
      }}
    />
  );

  if (isSidebar) {
    return (
      <>
        <div
          className={`${PANEL_BODY_FILL_CLASSNAME} h-full gap-4 overflow-hidden`}
          style={{ padding: mode === 'outline' ? 0 : '1rem' }}
        >
          {chaptersContent}
        </div>
        {addChapterModal}
        {sceneModal}
        {deleteChapterDialog}
        {deleteSceneDialog}
        {moveSceneDialog}
      </>
    );
  }

  return (
    <>
      {chaptersContent}
      {addChapterModal}
      {sceneModal}
      {deleteChapterDialog}
      {deleteSceneDialog}
      {moveSceneDialog}
    </>
  );
};
